import { usePageViewTracker } from 'context/PageViewTracker';
import React from 'react';
import { Helmet } from 'react-helmet';

const DEFAULT_TITLE = 'Insight Timer';
const DEFAULT_DESCRIPTION = '#1 Free Meditation App for Sleep, Relax & More';
const DEFAULT_IMAGE = 'https://insighttimer.com/image/insight-timer-logo-meta.png';

const PageMeta = ({
  url,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  ogImageUrl,
  ldJsonString,
  oEmbedUrl,
  nextPageUrl,
  previousPageUrl,
  ldJson,
  deepLink: deepLinkProp,
  children
}: {
  url?: string;
  title?: string;
  description?: string;
  ogImageUrl?: string;
  ldJsonString?: string;
  oEmbedUrl?: string;
  nextPageUrl?: string | null;
  previousPageUrl?: string | null;
  ldJson?: string;
  deepLink?: string;
  children?: React.ReactNode;
}) => {
  const { deepLink: contextDeepLink } = usePageViewTracker();
  const effectiveDeepLink = contextDeepLink ?? deepLinkProp;
  const effectiveUrl = url ?? window.location.href;
  const effectiveImage = ogImageUrl || DEFAULT_IMAGE;

  return (
    <Helmet>
      <meta property="fb:app_id" content="157891377582896" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@InsightTimer" />
      <meta name="twitter:app:name:iphone" content="Insight Timer" />
      <meta name="twitter:app:id:iphone" content="337472899" />
      <meta name="twitter:app:name:ipad" content="Insight Timer" />
      <meta name="twitter:app:id:ipad" content="365641524" />
      <meta name="twitter:app:name:googleplay" content="Insight Timer" />
      <meta
        name="twitter:app:id:googleplay"
        content="com.spotlightsix.zentimerlite2"
      />
      <meta name="author" content="Insight Network, Inc." />
      <meta
        name="copyright"
        content={`Insight Network, Inc. Copyright (c) ${new Date().getFullYear()}`}
      />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={effectiveImage} />
      <meta property="og:image:width" content="200" />
      <meta property="og:image:height" content="200" />
      <meta property="og:url" content={effectiveUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={effectiveImage} />
      <link rel="canonical" href={effectiveUrl} />
      {ldJson && <script type="application/ld+json">{ldJson}</script>}
      {effectiveDeepLink != null && (
        <meta name="branch:deeplink:$deeplink" content={effectiveDeepLink} />
      )}
      {effectiveDeepLink != null && (
        <meta name="branch:deeplink:$deeplink_v2" content={effectiveDeepLink} />
      )}
      {ldJsonString && (
        <script type="application/ld+json">{ldJsonString}</script>
      )}
      {oEmbedUrl && (
        <link
          rel="alternate"
          type="application/json+oembed"
          href={`https://widgets.insighttimer.com/services/oembed?url=${oEmbedUrl}`}
          title={title}
        />
      )}
      {nextPageUrl && <link rel="next" href={nextPageUrl} />}
      {previousPageUrl && <link rel="prev" href={previousPageUrl} />}
      {children}
    </Helmet>
  );
};

export default PageMeta;
